import React, { useEffect } from 'react';
import posed from 'react-pose';
import styled from '@emotion/styled';
import { connect } from 'react-redux';
import { RootState } from 'reducers/rootReducer';
import { settingsTextConfig } from 'config/messages';
import { sidebar, colors, toolbar } from 'config/paletteConfig';
import { Precision } from 'types/precision';
import { actions as settingsModalActions } from 'ducks/modal/settingsModal';
import { actions as settingsActions, selectors as settingsSelectors } from 'ducks/settings';
import { ReactComponent as IconClose } from 'assets/icons/x.svg';
import { appearFromTop, DisplayPose } from 'components/animations';
import { Modal, ButtonModalHeader as Button } from 'components/modal/Modal';
import { sidebarItemStyles, verticalSpacing } from 'components/sidebar/styles';
import RadioButton from 'components/elements/RadioButton';
import { feetToMeters } from 'helpers/utils';
import { actions as settingsPersistenceActions } from '../../ducks/persistence/settingsPersistence';

const flexRow = {
  display: 'flex',
  flexFlow: 'row wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
} as const;

const settingsStyles = {
  maxHeight: 'calc(100% - 64px)', // 100% height - 64 pixels for the header bar.
  'z-index': 1,
  right: 0,
  width: window.screen.width > 400 ? 500 : '100%',
  h2: {
    display: 'flex',
    justifyContent: 'space-between',
    fontWeight: 600,
    textTransform: 'none',

    span: {
      marginLeft: 0,
    },
  },
} as const;

const itemStyles = {
  width: 'auto',
  minHeight: 30,
} as const;

const ModalStyled = styled(Modal)(settingsStyles);
const ModalAnimated = posed(ModalStyled)(appearFromTop);

const Item = styled.li(sidebarItemStyles, itemStyles, flexRow, verticalSpacing);

// for Section Groups; Grid, Measurements, Preferences
const Label = styled.span({
  color: sidebar.item.color,
  fontWeight: 'bold',
  width: '100%',
});

// for simple field names, or subsection groups
const SubLabel = styled.div({
  color: sidebar.item.color,
  fontWeight: 400,
  width: '100%',

  '&:nth-of-type(1)': {
    paddingTop: 16,
  },
});

// The styles for the 4 Wall Preference Sub Label
const WallPreferenceLabel = styled.div({
  width: '100px',
  paddingRight: '10%',
  color: sidebar.item.color,
  fontWeight: 400,

  '&:nth-of-type(1)': {
    paddingTop: 16,
  },
});

// The styles for the 4 Wall Preference Div groups
const WallPreferenceDiv = styled.div({
  minWidth: '70%',
  display: 'flex',
  flexDirection: 'row',
});

const GridSubLabelContainer = styled.div({
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  display: 'flex',
  width: '100%',
});

const RadioGroup = styled.div(flexRow, {
  display: 'flex',
  flexDirection: window.screen.width > 500 ? 'row' : 'column',
  alignItems: 'baseline',
  width: '100%',
  margin: '15px 0',
});

const SegmentedControlContainer = styled.div({
  display: 'flex',
  width: '100%',
  border: `1px solid ${toolbar.border}`,
  borderRadius: '4px',
  overflow: 'hidden',
  margin: '15px 0',
});

const SegmentButton = styled.button<{ active: boolean }>(({ active }) => ({
  flex: 1,
  padding: '12px 0',
  textAlign: 'center',
  cursor: 'pointer',
  backgroundColor: active ? colors.blueDark : colors.white,
  color: active ? colors.white : colors.text,
  borderRight: `1px solid ${toolbar.border}`,
  fontSize: '14px',
  transition: 'all 0.2s ease',
  border: 0,
  margin: 0,

  '&:last-child': {
    borderRight: 'none',
  },

  '&:hover': {
    backgroundColor: active ? colors.blueDark : colors.bluishGray,
  },
  '&:focus-visible': {
    outline: `2px solid ${colors.blueDark}`,
    outlineOffset: '-2px',
  },
}));


const ZoomSettingWrapper = styled.div({
  width: '100%',
});

interface InputProps {
  readonly top: number;
  readonly pose?: DisplayPose;
}

interface StateProps {
  readonly gridLines: string;
  readonly precision: Precision;
  readonly unitOfMeasure: string;
  readonly gridSizeInFeet: any;
  readonly settingsModalForm: any;
  readonly exteriorWallDesktop: string;
  readonly exteriorWallMobile: string;
  readonly interiorWallDesktop: string;
  readonly interiorWallMobile: string;
  readonly zoomSpeed: number;
}

interface ActionProps {
  readonly close: () => void;
  readonly loadSettings: typeof settingsPersistenceActions.loadSettings;
  readonly updateSetting: typeof settingsPersistenceActions.updateSetting;
  readonly switchGridShow: typeof settingsActions.switchGridShow;
  readonly switchPrecision: typeof settingsActions.switchPrecision;
  readonly switchUnitOfMeasurement: typeof settingsActions.switchUnitOfMeasurement;
  readonly updateLastSetting: typeof settingsActions.updateLastSetting;
  readonly switchExteriorDesktop: typeof settingsActions.switchExteriorDesktop;
  readonly switchExteriorMobile: typeof settingsActions.switchExteriorMobile;
  readonly switchInteriorDesktop: typeof settingsActions.switchInteriorDesktop;
  readonly switchInteriorMobile: typeof settingsActions.switchInteriorMobile;
  readonly setZoomSpeed: typeof settingsActions.setZoomSpeed;
}

type Props = InputProps & StateProps & ActionProps;

const SettingsModal: React.FC<Props> = ({
  close, top, pose, loadSettings, settingsModalForm,
  switchGridShow, precision, switchUnitOfMeasurement,
  unitOfMeasure, switchPrecision, gridSizeInFeet,
  gridLines, updateLastSetting, updateSetting, switchExteriorDesktop, switchExteriorMobile, exteriorWallDesktop, exteriorWallMobile,
  interiorWallDesktop, interiorWallMobile, switchInteriorDesktop, switchInteriorMobile, zoomSpeed, setZoomSpeed
}) => {
  useEffect(() => {
    loadSettings();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (settingsModalForm.length) {
      const gridScaleSetting = settingsModalForm.find((setting: any) => setting.name === 'gridSizeInFeet');
      updateLastSetting({ uuid: gridScaleSetting.uuid, value: gridSizeInFeet });
      updateSetting();
    }
  }, [gridSizeInFeet]); // eslint-disable-line

  const handleUpdateLastSetting = (uuid: string, name: string, event: any): void => {
    if (name === 'gridLines' && event) {
      switchGridShow(String((event.target as HTMLSelectElement).value) as string);
      updateLastSetting({ uuid, value: String((event.target as HTMLSelectElement).value) as string });
    }
    if (name === 'unitOfMeasurement' && event) {
      switchUnitOfMeasurement(String((event.target as HTMLSelectElement).value) as string);
      updateLastSetting({ uuid, value: String((event.target as HTMLSelectElement).value) as string });
    }
    if (name === 'precision' && event) {
      switchPrecision(Number((event.target as HTMLSelectElement).value) as Precision);
      updateLastSetting({ uuid, value: String((event.target as HTMLSelectElement).value) as string });
    }
    if (name === 'exteriorWallDesktop' && event) {
      switchExteriorDesktop(String((event.target as HTMLSelectElement).value) as string);
      updateLastSetting({ uuid, value: String((event.target as HTMLSelectElement).value) as string });
    }
    if (name === 'exteriorWallMobile' && event) {
      switchExteriorMobile(String((event.target as HTMLSelectElement).value) as string);
      updateLastSetting({ uuid, value: String((event.target as HTMLSelectElement).value) as string });
    }
    if (name === 'interiorWallDesktop' && event) {
      switchInteriorDesktop(String((event.target as HTMLSelectElement).value) as string);
      updateLastSetting({ uuid, value: String((event.target as HTMLSelectElement).value) as string });
    }
    if (name === 'interiorWallMobile' && event) {
      switchInteriorMobile(String((event.target as HTMLSelectElement).value) as string);
      updateLastSetting({ uuid, value: String((event.target as HTMLSelectElement).value) as string });
    }
    updateSetting();
  };

  const applyZoomSpeed = (value: number): void => {
    setZoomSpeed(value);
    localStorage.setItem('zoomSpeed', String(value));
  };

  const gridScaleUnitConversion = (): string => {
    if (unitOfMeasure === 'decimalFeet') return ' Feet';
    if (unitOfMeasure === 'feet') return ' Feet';
    if (feetToMeters(gridSizeInFeet) % 1 === 0) return ' Meter';
    return 'Meters';
  };

  const gridScaleValue = (): string => {
    const nearestThousandth = +(unitOfMeasure === 'meters' ? feetToMeters(gridSizeInFeet) : gridSizeInFeet).toFixed(3);
    return `${nearestThousandth} ${gridScaleUnitConversion()}`;
  };

  return (
    <ModalAnimated top={top} right={28} pose={pose}>
      <h2>
        <Button onClick={close}>
          <IconClose />
        </Button>
        <span>{settingsTextConfig.heading}</span>
        <div />
      </h2>
      <ul style={{ overflowY: 'scroll' }}>
        {/* Grid */}
        <Item>
          <Label>{settingsTextConfig.grid.label}</Label>
          {settingsModalForm.map((setting: any) => {
            if (setting.type === 'static' && setting.name === 'gridSizeInFeet') {
              return (
                <GridSubLabelContainer key={setting.id}>
                  <SubLabel>{settingsTextConfig.grid.subLabelGridScale}</SubLabel>
                  <span>
                    {' '}
                    {gridScaleValue()}
                  </span>
                </GridSubLabelContainer>
              );
            }
            return undefined;
          })}
          {settingsModalForm.map((setting: any) => {
            if (setting.type === 'radio' && setting.name === 'gridLines') {
              return (
                <div key={setting.id}>
                  <SubLabel>{settingsTextConfig.grid.subLabelGridLines}</SubLabel>
                  <RadioGroup
                    onChange={(event: any) => handleUpdateLastSetting(setting.uuid, setting.name, event)}
                  >
                    {setting.options.map((option: any) => (
                      <RadioButton key={option.id} label={option.label} id={setting.id} value={option.value} checked={gridLines === option.value} />
                    ))}
                  </RadioGroup>
                </div>
              );
            }
            return undefined;
          })}
        </Item>

        {/* Zoom */}
        <Item>
          <Label>{settingsTextConfig.zoom.label}</Label>
          <ZoomSettingWrapper>
            <SubLabel>{settingsTextConfig.zoom.subLabelSpeed}</SubLabel>
            <SegmentedControlContainer>
              <SegmentButton
                active={zoomSpeed === 0.25}
                aria-pressed={zoomSpeed === 0.25}
                type="button"
                onClick={() => applyZoomSpeed(0.25)}
              >
                0.25x
              </SegmentButton>
              <SegmentButton
                active={zoomSpeed === 0.5}
                aria-pressed={zoomSpeed === 0.5}
                type="button"
                onClick={() => applyZoomSpeed(0.5)}
              >
                0.5x
              </SegmentButton>
              <SegmentButton
                active={zoomSpeed === 1}
                aria-pressed={zoomSpeed === 1}
                type="button"
                onClick={() => applyZoomSpeed(1)}
              >
                1x
              </SegmentButton>
              <SegmentButton
                active={zoomSpeed === 2}
                aria-pressed={zoomSpeed === 2}
                type="button"
                onClick={() => applyZoomSpeed(2)}
              >
                2x
              </SegmentButton>
              <SegmentButton
                active={zoomSpeed === 3}
                aria-pressed={zoomSpeed === 3}
                type="button"
                onClick={() => applyZoomSpeed(3)}
              >
                3x
              </SegmentButton>
            </SegmentedControlContainer>
          </ZoomSettingWrapper>
        </Item>

        {/* Measurements */}
        <Item>
          <Label>{settingsTextConfig.measurement.label}</Label>
          {settingsModalForm.map((setting: any) => {
            if (setting.type === 'radio' && setting.name === 'unitOfMeasurement') {
              return (
                <div key={setting.id}>
                  <SubLabel>{settingsTextConfig.measurement.subLabelUnit}</SubLabel>
                  <RadioGroup
                    onChange={(event: any) => handleUpdateLastSetting(setting.uuid, setting.name, event)}
                  >
                    {setting.options.map((option: any) => (
                      <RadioButton key={option.id} label={option.label} id={setting.id} value={option.value} checked={unitOfMeasure === option.value} />
                    ))}
                  </RadioGroup>
                </div>
              );
            }
            return undefined;
          })}
          {settingsModalForm.map((setting: any) => {
            if (setting.type === 'radio' && setting.name === 'precision') {
              return (
                <div key={setting.id}>
                  <SubLabel>{settingsTextConfig.measurement.subLabelPrecision}</SubLabel>
                  <RadioGroup
                    onChange={(event: any) => handleUpdateLastSetting(setting.uuid, setting.name, event)}
                  >
                    {setting.options.map((option: any) => (
                      <RadioButton key={option.id} label={option.label} id={setting.id} value={option.value} checked={`${precision}` === option.value} />
                    ))}
                  </RadioGroup>
                </div>
              );
            }
            return undefined;
          })}
        </Item>

        {/* Wall Tool Preferences */}
        <Item>
          <Label>{settingsTextConfig.measurement.subPreferences}</Label>
          <SubLabel>{settingsTextConfig.exteriorWall.subheader}</SubLabel>
          {settingsModalForm.map((setting: any) => {
            if (setting.type === 'radio' && setting.name === 'exteriorWallDesktop') {
              if (setting.options.length > 0 && setting.options[0].label === 'Disabled') {
                setting.options.reverse();
              }
              return (
                <WallPreferenceDiv key={setting.id}>
                  <WallPreferenceLabel>{settingsTextConfig.exteriorWall.subLabelDesktop}</WallPreferenceLabel>
                  <RadioGroup
                    onChange={(event: any) => handleUpdateLastSetting(setting.uuid, setting.name, event)}
                  >
                    {setting.options.map((option: any) => (
                      <RadioButton key={option.id} label={option.label} id={setting.id} value={option.value} checked={exteriorWallDesktop === option.value} />
                    ))}
                  </RadioGroup>
                </WallPreferenceDiv>
              );
            }
            return undefined;
          })}

          {settingsModalForm.map((setting: any) => {
            if (setting.type === 'radio' && setting.name === 'exteriorWallMobile') {
              if (setting.options.length > 0 && setting.options[0].label === 'Disabled') {
                setting.options.reverse();
              }
              return (
                <WallPreferenceDiv key={setting.id}>
                  <WallPreferenceLabel>{settingsTextConfig.exteriorWall.subLabelMobile}</WallPreferenceLabel>
                  <RadioGroup
                    onChange={(event: any) => handleUpdateLastSetting(setting.uuid, setting.name, event)}
                  >
                    {setting.options.map((option: any) => (
                      <RadioButton key={option.id} label={option.label} id={setting.id} value={option.value} checked={exteriorWallMobile === option.value} />
                    ))}
                  </RadioGroup>
                </WallPreferenceDiv>
              );
            }
            return undefined;
          })}

          <SubLabel>{settingsTextConfig.interiorWall.subheader}</SubLabel>
          {settingsModalForm.map((setting: any) => {
            if (setting.type === 'radio' && setting.name === 'interiorWallDesktop') {
              if (setting.options.length > 0 && setting.options[0].label === 'Disabled') {
                setting.options.reverse();
              }
              return (
                <WallPreferenceDiv key={setting.id}>
                  <WallPreferenceLabel>{settingsTextConfig.interiorWall.subLabelDesktop}</WallPreferenceLabel>
                  <RadioGroup
                    onChange={(event: any) => handleUpdateLastSetting(setting.uuid, setting.name, event)}
                  >
                    {setting.options.map((option: any) => (
                      <RadioButton key={option.id} label={option.label} id={setting.id} value={option.value} checked={interiorWallDesktop === option.value} />
                    ))}
                  </RadioGroup>
                </WallPreferenceDiv>
              );
            }
            return undefined;
          })}

          {settingsModalForm.map((setting: any) => {
            if (setting.type === 'radio' && setting.name === 'interiorWallMobile') {
              if (setting.options.length > 0 && setting.options[0].label === 'Disabled') {
                setting.options.reverse();
              }
              return (
                <WallPreferenceDiv key={setting.id}>
                  <WallPreferenceLabel>{settingsTextConfig.interiorWall.subLabelMobile}</WallPreferenceLabel>
                  <RadioGroup
                    onChange={(event: any) => handleUpdateLastSetting(setting.uuid, setting.name, event)}
                  >
                    {setting.options.map((option: any) => (
                      <RadioButton key={option.id} label={option.label} id={setting.id} value={option.value} checked={interiorWallMobile === option.value} />
                    ))}
                  </RadioGroup>
                </WallPreferenceDiv>
              );
            }
            return undefined;
          })}
        </Item>

      </ul>
    </ModalAnimated>
  );
};

export default connect(
  (state: RootState): StateProps => ({
    gridLines: settingsSelectors.gridLines(state),
    precision: settingsSelectors.getPrecision(state),
    unitOfMeasure: settingsSelectors.getUnitOfMeasure(state),
    gridSizeInFeet: settingsSelectors.getGridSizeInFeet(state),
    settingsModalForm: settingsSelectors.getSettingsModalForm(state),
    exteriorWallDesktop: settingsSelectors.getExteriorDesktop(state),
    exteriorWallMobile: settingsSelectors.getExteriorMobile(state),
    interiorWallDesktop: settingsSelectors.getInteriorDesktop(state),
    interiorWallMobile: settingsSelectors.getInteriorMobile(state),
    zoomSpeed: settingsSelectors.getZoomSpeed(state),
  }),
  {
    loadSettings: settingsPersistenceActions.loadSettings,
    updateSetting: settingsPersistenceActions.updateSetting,
    close: settingsModalActions.hide,
    switchGridShow: settingsActions.switchGridShow,
    switchPrecision: settingsActions.switchPrecision,
    switchUnitOfMeasurement: settingsActions.switchUnitOfMeasurement,
    updateLastSetting: settingsActions.updateLastSetting,
    switchExteriorDesktop: settingsActions.switchExteriorDesktop,
    switchExteriorMobile: settingsActions.switchExteriorMobile,
    switchInteriorDesktop: settingsActions.switchInteriorDesktop,
    switchInteriorMobile: settingsActions.switchInteriorMobile,
    setZoomSpeed: settingsActions.setZoomSpeed,
  },
)(SettingsModal);
